import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { hasCode, isApiError } from "@rekati/api";
import { Alert, BrandLogo, Button, Field, Input, Reveal, SignalTrace } from "@rekati/ui";
import IconEye from "~icons/solar/eye-linear";
import IconEyeClosed from "~icons/solar/eye-closed-linear";
import IconArrow from "~icons/solar/arrow-right-linear";
import { publicApi, staffApi } from "../../app/api";
import { HOME } from "../../app/access";
import { useI18n } from "../../content/i18n";
import { Signature } from "../shell/Signature";
import { staffSession, useStaffSession } from "./session-store";

/**
 * `/login` (38.2). One failure message for every cause, as the backend
 * answers: it must not say whether a login exists. The field is "Login", not
 * "Email": shared devices sign in as frontdesk_1 and the like.
 */
export function LoginPage() {
  const { t } = useI18n();
  const session = useStaffSession();
  const brand = useQuery({ queryKey: ["brand"], queryFn: publicApi.brand, staleTime: 30 * 60_000 }).data;
  const eventName =
    useQuery({ queryKey: ["event"], queryFn: publicApi.sessions, staleTime: 5 * 60_000 }).data?.event.name ??
    "Powering the Future of Healthcare";
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loginRef = useRef<HTMLInputElement>(null);

  useEffect(() => loginRef.current?.focus(), []);

  const signIn = useMutation({
    mutationFn: () => staffApi.login(login.trim(), password),
    onSuccess: (res) => staffSession.set({ token: res.token, expiresAt: res.expires_at, staff: res.staff }),
    onError: (e) => {
      if (hasCode(e, "INVALID_CREDENTIALS", "VALIDATION_FAILED")) setError(t("login.invalid"));
      else if (hasCode(e, "RATE_LIMITED"))
        setError(t("errors.rateLimited", { s: isApiError(e) ? (e.retryAfterSec ?? 60) : 60 }));
      else if (hasCode(e, "NETWORK", "TIMEOUT", "UNREACHABLE")) setError(t("errors.network"));
      else setError(t("errors.generic"));
    },
  });

  if (session) return <Navigate to={HOME[session.staff.role]} replace />;

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      {/* The event side: ink, the signal, the organiser, the platform's signature. */}
      <aside
        data-theme="dark"
        className="relative hidden flex-col justify-between overflow-hidden bg-background p-10 text-foreground lg:flex"
      >
        <BrandLogo
          src={brand?.organizer_logo_dark_url || brand?.organizer_logo_url}
          name={brand?.organizer_name || "iForte"}
          className="h-7"
          textClassName="text-lg text-volt"
        />
        <div className="grid gap-8">
          <p className="max-w-[12ch] text-6xl leading-[0.95] font-semibold tracking-tight uppercase">
            {eventName.split(/\s+/).map((w, i) => (
              <span key={i} className={/healthcare/i.test(w) ? "text-volt" : undefined}>
                {w}{" "}
              </span>
            ))}
          </p>
          <SignalTrace className="h-auto" />
        </div>
        <Signature dark />
      </aside>

      <main id="main" className="flex items-center justify-center px-5 py-12">
        <Reveal className="grid w-full max-w-sm gap-8">
          <div className="grid gap-2">
            <p className="font-mono text-xs tracking-[0.18em] text-muted-foreground uppercase">
              {t("login.kicker")}
            </p>
            <h1 className="text-4xl font-semibold tracking-tight">{t("login.title")}</h1>
            <p className="text-muted-foreground">{t("login.body")}</p>
          </div>
          <form
            noValidate
            className="grid gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (!login.trim() || !password) return setError(t("login.invalid"));
              signIn.mutate();
            }}
          >
            <Field label={t("login.login")}>
              {({ id, describedBy }) => (
                <Input
                  ref={loginRef}
                  id={id}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  aria-describedby={describedBy}
                  aria-invalid={error ? true : undefined}
                />
              )}
            </Field>
            <Field label={t("login.password")}>
              {({ id }) => (
                <div className="relative">
                  <Input
                    id={id}
                    type={reveal ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((r) => !r)}
                    aria-label={reveal ? t("login.hide") : t("login.show")}
                    aria-pressed={reveal}
                    className="absolute top-1/2 right-1 grid size-9 -translate-y-1/2 place-items-center rounded-sm text-muted-foreground hover:text-foreground"
                  >
                    {reveal ? (
                      <IconEyeClosed className="size-5" aria-hidden="true" />
                    ) : (
                      <IconEye className="size-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              )}
            </Field>
            {error ? <Alert tone="danger">{error}</Alert> : null}
            <Button type="submit" variant="volt" size="lg" loading={signIn.isPending}>
              {t("login.submit")}
              <IconArrow aria-hidden="true" />
            </Button>
          </form>
          {/* On a phone the event side is gone; the signature moves under the form. */}
          <Signature className="border-t border-border pt-5 lg:hidden" />
        </Reveal>
      </main>
    </div>
  );
}
